/**
 * Notification keyword utilities
 */

export interface NotificationKeyword {
  id: string;
  user_id: string;
  workspace_id: string;
  keyword: string;
}

export interface KeywordMatch {
  keyword: string;
  index: number;
  length: number;
}

/**
 * Check if text contains a keyword match
 * Matching is case-insensitive and respects word boundaries
 */
export function findKeywordMatches(
  text: string,
  keywords: string[],
): KeywordMatch[] {
  const matches: KeywordMatch[] = [];

  for (const keyword of keywords) {
    // Create word boundary regex for exact word matching
    // Case insensitive
    const regex = new RegExp(`\\b${escapeRegex(keyword)}\\b`, "gi");
    let match;

    while ((match = regex.exec(text)) !== null) {
      matches.push({
        keyword: keyword.toLowerCase(),
        index: match.index,
        length: match[0].length,
      });
    }
  }

  return matches;
}

/**
 * Escape special regex characters
 */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Check if a keyword match should trigger notification
 * Keywords do NOT trigger in threads
 */
export function shouldNotifyKeyword(
  isThread: boolean,
  isJoinedChannel: boolean,
): boolean {
  // Keywords do NOT trigger in threads
  if (isThread) {
    return false;
  }

  // Keywords only match in joined channels
  if (!isJoinedChannel) {
    return false;
  }

  return true;
}

/**
 * Check if keyword matches partial word (should be rejected)
 */
export function isPartialWordMatch(
  text: string,
  keyword: string,
  matchIndex: number,
): boolean {
  // Check character before match
  if (matchIndex > 0) {
    const charBefore = text[matchIndex - 1];
    if (/\w/.test(charBefore)) {
      return true;
    }
  }

  // Check character after match
  const afterIndex = matchIndex + keyword.length;
  if (afterIndex < text.length) {
    const charAfter = text[afterIndex];
    if (/\w/.test(charAfter)) {
      return true;
    }
  }

  return false;
}

/**
 * Highlight keyword matches in text
 * Returns text with matches wrapped in highlight spans
 */
export function highlightKeywords(
  text: string,
  matches: KeywordMatch[],
): string {
  if (matches.length === 0) {
    return text;
  }

  // Sort matches by index descending to avoid offset issues
  const sortedMatches = [...matches].sort((a, b) => b.index - a.index);

  let result = text;
  for (const match of sortedMatches) {
    const before = result.substring(0, match.index);
    const matched = result.substring(match.index, match.index + match.length);
    const after = result.substring(match.index + match.length);
    result = `${before}<mark class="keyword-highlight">${matched}</mark>${after}`;
  }

  return result;
}

/**
 * Add a keyword to user's notification keywords
 */
export function addKeyword(
  keywords: NotificationKeyword[],
  keyword: NotificationKeyword,
): NotificationKeyword[] {
  return [...keywords, keyword];
}

/**
 * Remove a keyword from user's notification keywords
 */
export function removeKeyword(
  keywords: NotificationKeyword[],
  keywordId: string,
): NotificationKeyword[] {
  return keywords.filter((k) => k.id !== keywordId);
}
